import type { Vec } from "./vec";

export const ARENA_W = 28;
export const ARENA_H = 18;
export const MATCH_SECONDS = 120;
export const SCORE_LIMIT = 100;
export const UNIT_RADIUS = 0.42;

export type TeamId = 0 | 1;

export type ClassId =
  | "warrior"
  | "ranger"
  | "priest"
  | "cultist"
  | "psychic"
  | "assassin"
  | "dervish"
  | "ritualist"
  | "paragon"
  | "paladin"
  | "skeleton"
  | "golem"
  | "spirit";

export type HeroClassId = Exclude<ClassId, "skeleton" | "golem" | "spirit">;

export type UnitKind = "hero" | "corpse" | "minion" | "spirit";

export type DamageType = "physical" | "psychic" | "burning" | "bleed" | "poison";

export type ShieldKind = "any" | "psychic";

export type StatusPolarity = "condition" | "enchantment";

export type StatusId =
  | "bleeding"
  | "cripple"
  | "stagger"
  | "stun"
  | "maim"
  | "wound"
  | "poison"
  | "burning"
  | "regen"
  | "ire"
  | "curse"
  | "blind"
  | "silence"
  | "fleet"
  | "frenzy"
  | "invisible"
  | "empathy"
  | "retribution"
  | "spirit_arms"
  | "mystic_sweep"
  | "avatar"
  | "sanctuary"
  | "rally"
  | "tide"
  | "warded"
  | "faith";

export type AbilitySlot = "auto" | "skill" | "ult";

export type AbilityKind =
  | "meleeArc"
  | "projectile"
  | "dash"
  | "chargeLine"
  | "target"
  | "self"
  | "area"
  | "areaDelay"
  | "zone"
  | "swap"
  | "custom";

export type TargetRule = "foe" | "ally" | "any" | "self" | "allyOrSpirit" | "none";

export type PredId =
  | "immobile"
  | "fleeing"
  | "hasBleed"
  | "hasCripple"
  | "hasBurning"
  | "hasRegen"
  | "hasIre"
  | "hasRetribution"
  | "isEnchanted"
  | "notEnchanted"
  | "isCorpse"
  | "lowHp"
  | "facingSource"
  | "notFacingSource"
  | "moving"
  | "always";

export interface StatusDef {
  id: StatusId;
  name: string;
  polarity: StatusPolarity;
  pip: string;
  color: string;
  stack: "refresh" | "ignore";
  tickEvery: number;
  tickDamage: number;
  tickHeal: number;
  tickType: DamageType;
  tags: string[];
}

export interface StatusInst {
  id: StatusId;
  remaining: number;
  duration: number;
  sourceId: number;
  copy: boolean;
  tickAcc: number;
}

export interface ShieldLayer {
  id: string;
  kind: ShieldKind;
  amount: number;
  max: number;
  decayPerSec: number;
  regenPerSec: number;
  sourceId: number;
  tag: string;
}

export interface EffectOp {
  op:
    | "status"
    | "statusIf"
    | "stripPolarity"
    | "removeStatus"
    | "shield"
    | "interrupt"
    | "stealHealth";
  id?: StatusId;
  duration?: number;
  pred?: PredId;
  polarity?: StatusPolarity;
  amount?: number;
  decay?: number;
  shieldKind?: ShieldKind;
  tag?: string;
}

export interface AbilityDef {
  id: string;
  name: string;
  classId: ClassId;
  slot: AbilitySlot;
  description: string;
  cd: number;
  range: number;
  kind: AbilityKind;
  target: TargetRule;
  windup: number;
  windupMove: number;
  radius: number;
  arc: number;
  damage: number;
  splash: number;
  heal: number;
  damageType: DamageType;
  critIf?: PredId;
  bonusIf?: { pred: PredId; amount: number };
  onHit: EffectOp[];
  onCrit: EffectOp[];
  selfOnCast: EffectOp[];
  projectileSpeed: number;
  projectileRadius: number;
  projectileCount: number;
  projectileSpread: number;
  dashDistance: number;
  chargeSpeed: number;
  delay: number;
  zoneKind: string;
  zoneDuration: number;
}

export interface ClassDef {
  id: ClassId;
  name: string;
  role: string;
  law: string;
  color: string;
  maxHp: number;
  speed: number;
  basicCd: number;
  skill: string;
  ult: string;
  autos: string[];
  passives: string[];
  playable: boolean;
}

export interface CastState {
  abilityId: string;
  remaining: number;
  total: number;
  aimX: number;
  aimY: number;
  targetId: number | null;
}

export interface ChargeState {
  abilityId: string;
  kind: "line" | "slam";
  remaining: number;
  dirX: number;
  dirY: number;
  traveled: number;
  maxDist: number;
  speed: number;
}

export interface Unit {
  id: number;
  team: TeamId;
  name: string;
  classId: ClassId;
  kind: UnitKind;
  pos: Vec;
  prevPos: Vec;
  radius: number;
  hp: number;
  maxHp: number;
  facing: number;
  baseSpeed: number;
  isPlayer: boolean;
  dead: boolean;
  respawnIn: number;
  respawnSecs: number;
  spawn: Vec;
  ownerId: number | null;
  basicCd: number;
  basicCdMax: number;
  skillCd: number;
  skillCdMax: number;
  ultCd: number;
  ultCdMax: number;
  casting: CastState | null;
  charging: ChargeState | null;
  lockout: number;
  statuses: StatusInst[];
  shields: ShieldLayer[];
  lastAttackAt: number;
  lastMoveAt: number;
  stillFor: number;
  movedThisTick: boolean;
  seenAt: Record<number, number>;
  ambushGrace: Record<number, number>;
  state: Record<string, number>;
  damageDealt: number;
  damageTaken: number;
  healsDone: number;
  autoTargetId: number | null;
}

export interface Cover {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  hp: number;
  maxHp: number;
  destroyed: boolean;
}

export interface Wardstone {
  id: number;
  name: string;
  pos: Vec;
  radius: number;
  owner: TeamId | null;
  progress: number;
  capturing: TeamId | null;
}

export interface Projectile {
  id: number;
  ownerId: number;
  team: TeamId;
  pos: Vec;
  vel: Vec;
  radius: number;
  damage: number;
  remaining: number;
  abilityId: string;
  damageType: DamageType;
  onHit: EffectOp[];
  onCrit: EffectOp[];
  critIf?: PredId;
  bonusIf?: { pred: PredId; amount: number };
  guaranteedCrit: boolean;
}

export interface Pulse {
  id: number;
  pos: Vec;
  radius: number;
  life: number;
  maxLife: number;
  kind: "aoe" | "heal" | "slam" | "dash";
  team: TeamId;
}

export interface Zone {
  id: number;
  kind: string;
  pos: Vec;
  radius: number;
  remaining: number;
  team: TeamId;
  ownerId: number;
}

export interface Delay {
  id: number;
  kind: string;
  remaining: number;
  pos: Vec;
  radius: number;
  ownerId: number;
}

export interface CombatEvent {
  kind: "dmg" | "heal" | "miss" | "interrupt" | "kd" | "block" | "capture" | "crit" | "info";
  x: number;
  y: number;
  value: number;
  team: TeamId;
  text: string;
  age: number;
}

export interface PlayerInput {
  unitId: number;
  moveX: number;
  moveY: number;
  aimX: number;
  aimY: number;
  skill: boolean;
  ult: boolean;
  clickedId: number | null;
  clearTarget: boolean;
}

export interface World {
  time: number;
  duration: number;
  units: Unit[];
  cover: Cover[];
  stones: Wardstone[];
  projectiles: Projectile[];
  pulses: Pulse[];
  zones: Zone[];
  delays: Delay[];
  events: CombatEvent[];
  nextId: number;
  scores: [number, number];
  over: boolean;
  winner: TeamId | null;
  suddenDeath: boolean;
  rngState: number;
  endedReason: string;
  playerId: number;
  hookDepth: number;
}
